import { useEffect, useState, useCallback, useRef } from "react";
import { UserCog, Loader2, RefreshCw, Search, IdCard, Building2, ShieldCheck, ShieldAlert, Lock } from "lucide-react";
import { toast } from "sonner";
import api from "../../../lib/api";
import { useAuth } from "../../../lib/auth";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../ui/dialog";
import { useAutosaveOnBlur, AutosaveBadge } from "./useAutosaveOnBlur";
import { DispoBackButton } from "./DispoBackButton";
import { FeatureGuideTriangle } from "./FeatureGuideTriangle";
import { focusFirstMissing } from "../../../lib/formValidation";

// ---------- Kundendaten -------------------------------------------------------
const SALUTATIONS = ["Herr", "Frau", "Divers", "Firma", "Keine Angabe"];
// Jul-2026 (User-Wunsch) — Land bei der Kundenerfassung (EU-Handel / steuerfreier
// innergemeinschaftlicher Verkauf). EU-Länder zuerst, dann Rest.
const COUNTRIES = [
    "Deutschland", "Österreich", "Belgien", "Bulgarien", "Dänemark", "Estland", "Finnland",
    "Frankreich", "Griechenland", "Irland", "Italien", "Kroatien", "Lettland", "Litauen",
    "Luxemburg", "Malta", "Niederlande", "Polen", "Portugal", "Rumänien", "Schweden",
    "Slowakei", "Slowenien", "Spanien", "Tschechien", "Ungarn", "Zypern",
    "Schweiz", "Großbritannien", "Norwegen", "Sonstiges Ausland",
];

export function CustomerDataModal({ listingId, employees = [], empLoaded = true, initialCustomer = null, onBack, onClose, onSaved }) {
    const { user } = useAuth();
    const [emps, setEmps] = useState(employees?.length ? employees : []);
    const [empReady, setEmpReady] = useState(Boolean(employees?.length) || empLoaded);
    const [form, setForm] = useState({ salutation: "Herr", first_name: "", last_name: "", birth_date: "", company_name: "", is_b2b_dealer: false, email: "", phone: "", street: "", house_number: "", postal_code: "", city: "", country: "Deutschland", tax_id: "", customer_kind: "", customer_user_id: "", transaction_code: "" });
    const [saving, setSaving] = useState(false);
    const [custNumber, setCustNumber] = useState("");
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    // Jun-2026 (User-Wunsch) — Firmen-Suchfeld (registrierte User & Gewerbekunden).
    const [firmaResults, setFirmaResults] = useState([]);
    const [firmaSearching, setFirmaSearching] = useState(false);
    // Jul-2026 (User-Wunsch) — USt-IdNr.-Prüfung (eVATR/BZSt) direkt bei der Erfassung.
    const [vatCheck, setVatCheck] = useState(null);
    const [vatChecking, setVatChecking] = useState(false);
    const runVatCheck = async () => {
        setVatChecking(true);
        try {
            const r = await api.post("/tax/evatr-check", {
                vat: form.tax_id,
                company_name: form.company_name || undefined,
                city: form.city || undefined,
                plz: form.postal_code || undefined,
                street: `${form.street || ""} ${form.house_number || ""}`.trim() || undefined,
                listing_id: listingId,
            });
            const s = {
                id: r.data.id, valid: r.data.valid, status: r.data.status,
                meldung: r.data.meldung, vat: `${r.data.country_code || ""}${r.data.vat_number || ""}`,
                name: r.data.name, source: r.data.source, checked_at: r.data.checked_at,
            };
            touched.current = true;
            setVatCheck(s);
            setForm((p) => ({ ...p, vat_check: s }));
            if (r.data.valid) toast.success(`USt-IdNr. ${s.vat} ist gültig ✓${r.data.name ? ` · ${r.data.name}` : ""}`);
            else if (r.data.valid === false) toast.error(r.data.meldung || "USt-IdNr. ungültig");
            else toast.warning(r.data.meldung || "Prüfung derzeit nicht möglich — bitte später erneut.");
        } catch (e) {
            toast.error(e.response?.data?.detail || "USt-IdNr.-Prüfung fehlgeschlagen");
        } finally { setVatChecking(false); }
    };
    // Sobald der Nutzer irgendwo tippt/auswählt, darf der (ggf. langsame)
    // Mount-Fetch die Eingaben NICHT mehr überschreiben (Race → „Eingabe-Freeze",
    // getippte Zeichen verschwinden). `touched` schützt davor.
    const touched = useRef(false);
    // Bereits gespeicherte Kundendaten einmalig vorbefüllen (inkl. Geburtsdatum
    // für die Sicherheitsabfrage des Upload-Portals).
    useEffect(() => {
        let alive = true;
        api.get(`/listings/${listingId}`).then((r) => {
            if (!alive || touched.current) return;
            // WICHTIG (Chef-Anweisung): bereits erfasster Verkäufer wird angezeigt
            // und bleibt erhalten — kein Zurückfallen auf „self".
            const sp = r.data?.dispo_salesperson;
            if (sp?.id) { setExistingSeller(sp); setSalespersonId(sp.id); }
            const c = r.data?.dispo_customer;
            if (c && (c.first_name || c.email || c.last_name)) {
                setForm((p) => ({ ...p, ...Object.fromEntries(Object.keys(p).map((k) => [k, c[k] ?? p[k]])) }));
                if (c.vat_check) { setVatCheck(c.vat_check); setForm((p) => ({ ...p, vat_check: c.vat_check })); }
                if (c.customer_number) setCustNumber(c.customer_number);
                if (!sp?.id && c.salesperson_id) setSalespersonId(c.salesperson_id);
            }
        }).catch(() => {});
        return () => { alive = false; };
    }, [listingId]);

    // Bestandskunden-Suche (Name, Firma, E-Mail, Telefon, Kundennummer)
    const runSearch = async (val) => {
        touched.current = true;
        setQuery(val);
        if ((val || "").trim().length < 2) { setResults([]); return; }
        setSearching(true);
        try {
            const r = await api.get(`/dealer/customers/search`, { params: { q: val.trim() } });
            setResults(r.data?.customers || []);
        } catch { /* */ } finally { setSearching(false); }
    };
    const selectCustomer = (c) => {
        setForm((p) => ({ ...p, salutation: c.salutation || p.salutation, first_name: c.first_name || "", last_name: c.last_name || "", birth_date: c.birth_date || "", company_name: c.company_name || "", is_b2b_dealer: !!c.is_b2b_dealer, email: c.email || "", phone: c.phone || "", street: c.street || "", house_number: c.house_number || "", postal_code: c.postal_code || "", city: c.city || "", country: c.country || "Deutschland", tax_id: c.tax_id || "" }));
        setCustNumber(c.customer_number || "");
        setResults([]); setQuery("");
        toast.success(`Bestandskunde übernommen${c.customer_number ? ` · ${c.customer_number}` : ""}`);
    };
    // Default-Verkäufer = eingeloggter User ("self"); Firmen-Login (b2b) MUSS
    // einen echten Mitarbeiter wählen (Aug-2026, User-Wunsch — Pflichtauswahl).
    const isCompanyLogin = user?.role === "b2b";
    const [salespersonId, setSalespersonId] = useState(user?.role === "b2b" ? "" : "self");
    // Nov-2026 (User-Wunsch): Verkäufer nach Erstauswahl gesperrt — Filialleitung
    // muss bewusst entsperren, um zu ändern.
    const [sellerUnlocked, setSellerUnlocked] = useState(false);
    // WICHTIG (Chef-Anweisung): der Verkäufer, der den Verkaufsprozess erfasst
    // hat, bleibt erhalten — nur Filialleiter/Inhaber dürfen manuell ändern.
    const [existingSeller, setExistingSeller] = useState(null);
    const update = useCallback((k, v) => { touched.current = true; setForm((p) => ({ ...p, [k]: v })); }, []);

    // Aug-2026 (Bugfix) — zweite Prefill-Quelle: bereits geladene Kundendaten vom
    // Parent (DispositionActions.state.customer), falls der eigene Mount-Fetch
    // raced/fehlschlägt (z. B. direkt nach B2B-Vermittlung via ?dispo=sell).
    useEffect(() => {
        const c = initialCustomer;
        if (!c || touched.current) return;
        if (c.first_name || c.email || c.last_name) {
            setForm((p) => ({ ...p, ...Object.fromEntries(Object.keys(p).map((k) => [k, c[k] ?? p[k]])) }));
            if (c.vat_check) setVatCheck(c.vat_check);
            if (c.customer_number) setCustNumber(c.customer_number);
            if (c.salesperson_id) setSalespersonId(c.salesperson_id);
        }
    }, [initialCustomer]);

    // Jun-2026 (User-Wunsch) — Firmen-Suche (Anrede „Firma"): registrierte User
    // (Priorität) + Gewerbekunden. Freitext ohne Treffer = Gewerbetreibender.
    const runFirmaSearch = async (val) => {
        if ((val || "").trim().length < 2) { setFirmaResults([]); return; }
        setFirmaSearching(true);
        try {
            const r = await api.get(`/dealer/customers/search-firma`, { params: { q: val.trim() } });
            setFirmaResults(r.data?.results || []);
        } catch { /* */ } finally { setFirmaSearching(false); }
    };
    const updateCompany = (val) => {
        touched.current = true;
        setForm((p) => ({ ...p, company_name: val, customer_kind: val.trim() ? "gewerbe" : "", customer_user_id: "", transaction_code: "" }));
        runFirmaSearch(val);
    };
    const selectFirma = (r) => {
        setForm((p) => ({
            ...p,
            company_name: r.company_name || p.company_name,
            first_name: r.first_name || p.first_name,
            last_name: r.last_name || p.last_name,
            email: r.email || p.email,
            phone: r.phone || p.phone,
            street: r.street || p.street,
            house_number: r.house_number || p.house_number,
            postal_code: r.postal_code || p.postal_code,
            city: r.city || p.city,
            country: r.country || p.country,
            tax_id: r.tax_id || p.tax_id,
            birth_date: r.birth_date || p.birth_date,
            customer_kind: r.kind,
            customer_user_id: r.user_id || "",
            transaction_code: r.transaction_code || "",
            is_b2b_dealer: r.kind === "registered_user" ? false : p.is_b2b_dealer,
        }));
        if (r.customer_number) setCustNumber(r.customer_number);
        setFirmaResults([]);
        toast.success(r.kind === "registered_user"
            ? `Registrierter User übernommen${r.transaction_code ? ` · ${r.transaction_code}` : ""}`
            : "Gewerbekunde übernommen");
    };

    // Parent (DispositionActions) lädt Mitarbeiter zuverlässig und reicht sie als
    // Prop durch. Wir koppeln Liste + Ready-State REAKTIV an die Props (nicht nur
    // beim ersten Render), damit das Modal die Daten auch dann erhält, wenn es vor
    // Abschluss des Parent-Fetchs geöffnet wird (Radix-Portal-Mount-Race).
    useEffect(() => { if (employees?.length) setEmps(employees); }, [employees]);
    useEffect(() => { if (empLoaded) setEmpReady(true); }, [empLoaded]);
    // Zusätzlicher eigener Fetch als Fallback (überschreibt nur bei Treffern).
    useEffect(() => {
        let alive = true;
        api.get("/dealer/employees")
            .then((r) => { if (alive && r.data?.items?.length) setEmps(r.data.items); })
            .catch(() => {})
            .finally(() => { if (alive) setEmpReady(true); });
        return () => { alive = false; };
    }, []);

    const resolveSalesperson = () => {
        if (!salespersonId) return {};
        if (salespersonId === "self") {
            return { salesperson_id: user?.id || "self", salesperson_name: user?.name || user?.company_name || "Ich" };
        }
        if (existingSeller?.id === salespersonId) {
            return { salesperson_id: existingSeller.id, salesperson_name: existingSeller.name || "" };
        }
        const emp = emps.find((e) => e.id === salespersonId);
        return { salesperson_id: salespersonId, salesperson_name: emp?.name || "" };
    };

    // WICHTIG (Chef-Anweisung): erfasster Verkäufer wird NIE stillschweigend
    // mitgesendet/überschrieben — nur eine bewusste Änderung durch Filialleiter/
    // Inhaber geht mit explizitem Flag ans Backend.
    const salespersonPayload = () => {
        if (existingSeller?.id) {
            if (!canChangeSeller || !sellerUnlocked || salespersonId === existingSeller.id) return {};
            return { ...resolveSalesperson(), salesperson_change: true };
        }
        return resolveSalesperson();
    };

    const selfLabel = `${user?.name || user?.company_name || "Ich"} (ich)`;
    // Jul-2026 (Chef-Anweisung): Verkäufer nur durch Filialleiter oder
    // Inhaber/Geschäftsführer änderbar (NICHT Verkaufsleiter, nicht das System).
    const canChangeSeller = user?.role === "b2b" || user?.role === "admin" ||
        ["filialleiter", "geschaeftsfuehrer", "inhaber"].includes(user?.employee_function);

    // Jul-2026 (User-Wunsch, Phase 3 Abteilungen) — Verkäufer-Auswahl über die
    // effektiven Module des Mitarbeiters (aus seiner Abteilung abgeleitet):
    // wählbar sind Träger von Verkauf/Disposition/Buchhaltung/Gebiets-,
    // Verkaufs-, Bereichsleitung/Geschäftsführung. Bestands-Mitarbeiter ohne
    // Module fallen auf die alte Funktions-Ausschlussliste zurück.
    const SELLER_MODULES = ["verkauf", "disposition", "buchhaltung", "gebietsleitung", "verkaufsleitung", "bereichsleitung", "geschaeftsfuehrung"];
    const EXCLUDED_SELLER_FN = ["mechaniker", "kundendienst", "lager"];
    const sellerEmps = (emps || []).filter((e) => {
        const mods = Array.isArray(e.modules) ? e.modules : null;
        if (mods && mods.length) return mods.some((m) => SELLER_MODULES.includes(m));
        // Fallback (kein Modul bekannt): alte Funktions-Logik.
        if (EXCLUDED_SELLER_FN.includes((e.employee_function || "").toLowerCase())) return false;
        const perms = Array.isArray(e.permissions) ? e.permissions : null;
        if (perms && perms.length && !perms.includes("dispo")) return false;
        return true;
    });

    // Jul-2026 (Fix) — Autosave (onBlur) und expliziter „Speichern & weiter"-Klick
    // feuerten gleichzeitig zwei PATCHes auf denselben Endpunkt (Race → ClientDisconnect,
    // Spinner blieb hängen). Wir serialisieren die Speichervorgänge über eine In-Flight-Promise.
    const inFlightRef = useRef(null);
    const persist = async ({ silent = false, advance = false } = {}) => {
        if (inFlightRef.current) {
            if (silent) return false;               // Autosave überspringen — es läuft bereits ein Save
            try { await inFlightRef.current; } catch { /* */ }  // expliziter Save wartet auf den laufenden
        }
        // Autosave (silent) bleibt kulant (nur Vorname + E-Mail), damit Zwischenstände
        // gespeichert werden. Beim expliziten Speichern sind ALLE Felder Pflicht
        // (außer Bestandskunden-Suche & Kundennummer). Firma + Steuernummer nur bei „Firma".
        if (silent) {
            if (!form.first_name.trim() || !form.email.trim()) return false;
        } else {
            const req = [
                ["first_name", "Vorname", "dispo-cust-first"], ["last_name", "Nachname", "dispo-cust-last"],
                ["birth_date", "Geburtsdatum", "dispo-cust-birthdate"],
                ["email", "E-Mail", "dispo-cust-email"], ["phone", "Telefon", "dispo-cust-phone"],
                ["street", "Straße", "dispo-cust-street"], ["house_number", "Nr.", "dispo-cust-house"],
                ["postal_code", "PLZ", "dispo-cust-plz"], ["city", "Ort", "dispo-cust-city"],
            ];
            const missing = [];
            if (!form.salutation) missing.push({ ok: false, label: "Anrede", testid: "dispo-cust-salutation" });
            req.forEach(([k, label, testid]) => {
                if (!String(form[k] || "").trim()) missing.push({ ok: false, label, testid });
            });
            if (form.salutation === "Firma") {
                if (!String(form.company_name || "").trim()) missing.push({ ok: false, label: "Firma", testid: "dispo-cust-company" });
                if (!String(form.tax_id || "").trim()) missing.push({ ok: false, label: "Steuernummer", testid: "dispo-cust-tax" });
            }
            // Aug-2026 (User-Wunsch) — Firmen-Login: Verkäufer ist Pflicht.
            if (isCompanyLogin && !existingSeller?.id && (!salespersonId || salespersonId === "self")) {
                missing.push({ ok: false, label: "Verkäufer (Pflicht bei Firmen-Login)", testid: "dispo-cust-salesperson" });
            }
            if (missing.length) { focusFirstMissing(missing); return false; }
        }
        if (!silent) setSaving(true);
        const run = (async () => {
            try {
                const payload = { ...form, ...salespersonPayload(), customer_number: custNumber || undefined };
                const r = await api.patch(`/listings/${listingId}/dispo/customer-data`, payload);
                if (r.data?.customer?.customer_number) setCustNumber(r.data.customer.customer_number);
                if (r.data?.salesperson?.id) setExistingSeller(r.data.salesperson);
                if (!silent) {
                    toast.success("Kundendaten gespeichert");
                    autosave.markSaved();
                    // Auto-Kette (User-Wunsch): Parent öffnet direkt „Zubehör & DL".
                    if (advance) onSaved?.(r.data.customer);
                }
                return true;
            } catch (e) {
                if (!silent) toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
                return false;
            } finally { if (!silent) setSaving(false); }
        })();
        inFlightRef.current = run;
        try { return await run; } finally { if (inFlightRef.current === run) inFlightRef.current = null; }
    };
    const autosave = useAutosaveOnBlur(
        () => persist({ silent: true }),
        () => JSON.stringify({ form, salespersonId }),
    );
    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent onPointerDownOutside={(e) => e.preventDefault()} onInteractOutside={(e) => e.preventDefault()} className="w-[96vw] max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="dispo-modal-customer" onBlur={autosave.onBlur}>
                <FeatureGuideTriangle guideKey="customer" />
                <DialogHeader><DialogTitle className="flex items-center gap-2"><UserCog className="w-5 h-5" /> Käufer-Stammdaten ergänzen<span className="ml-auto text-[10px] font-bold uppercase tracking-wider text-gray-400 mr-6">Schritt 1 von 6</span></DialogTitle><DialogDescription>Käufer auswählen oder erfassen und Verkäufer zuordnen.</DialogDescription></DialogHeader>
                <AutosaveBadge status={autosave.status} savedAt={autosave.savedAt} />

                {/* Jun-2026 (User-Wunsch) — Bestandskunden-Suche & Verkäufer nebeneinander (eine Zeile) */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-1">
                <div className="bg-sky-50 border border-sky-200 rounded-sm p-3" data-testid="dispo-cust-search-block">
                    <div className="flex items-center justify-between gap-2 mb-1">
                        <Label className="text-xs font-bold uppercase tracking-wider text-sky-800 flex items-center gap-1.5"><Search className="w-3.5 h-3.5" /> Bestandskunde suchen</Label>
                        {custNumber && <span className="text-[11px] font-bold text-sky-700 flex items-center gap-1" data-testid="dispo-cust-number"><IdCard className="w-3.5 h-3.5" /> {custNumber}</span>}
                    </div>
                    <div className="relative">
                        <Input value={query} onChange={(e) => runSearch(e.target.value)} placeholder="Name, Firma, E-Mail, Tel., Kundennr.…" className="h-9 text-sm" data-testid="dispo-cust-search-input" />
                        {searching && <Loader2 className="w-4 h-4 animate-spin absolute right-2 top-2.5 text-gray-400" />}
                        {results.length > 0 && (
                            <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-sm shadow-lg max-h-56 overflow-y-auto" data-testid="dispo-cust-search-results">
                                {results.map((c) => (
                                    <button key={c.id} type="button" onClick={() => selectCustomer(c)} className="w-full text-left px-3 py-2 hover:bg-sky-50 border-b border-gray-100 last:border-0" data-testid={`dispo-cust-search-result-${c.id}`}>
                                        <div className="text-sm font-bold text-gray-800">{c.company_name || `${c.first_name} ${c.last_name}`.trim()} {c.customer_number ? <span className="text-[10px] font-mono-num text-sky-600">· {c.customer_number}</span> : null}</div>
                                        <div className="text-[11px] text-gray-500">{[c.email, c.phone, c.city].filter(Boolean).join(" · ")}</div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Phase 2 — Verkäufer-Auswahl (Default = eingeloggter User).
                    Nov-2026 (User-Wunsch): nach Erstauswahl FESTGESCHRIEBEN —
                    Anzeige als gesperrtes Feld; nur Filialleitung kann per
                    bewusstem Klick entsperren und ändern. */}
                <div className="bg-orange-50 border border-orange-200 rounded-sm p-3" data-testid="dispo-cust-salesperson-block">
                    <Label className="text-xs font-bold uppercase tracking-wider text-orange-800 flex items-center gap-1.5"><UserCog className="w-3.5 h-3.5" /> Verkäufer</Label>
                    {existingSeller?.id && !sellerUnlocked ? (
                        <div className="mt-1 w-full border border-gray-300 rounded-sm px-2 py-2 text-sm bg-gray-100 text-gray-700 flex items-center justify-between gap-2" data-testid="dispo-cust-salesperson-locked">
                            <span className="inline-flex items-center gap-1.5 min-w-0">
                                <Lock className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                                <b className="truncate">{existingSeller.name || "Erfassender Verkäufer"}</b>
                                <span className="text-gray-500 shrink-0">(hat den Verkauf erfasst)</span>
                            </span>
                            {canChangeSeller && (
                                <button type="button" onClick={() => setSellerUnlocked(true)}
                                    className="text-[11px] font-bold underline text-orange-800 hover:text-orange-900 shrink-0"
                                    data-testid="dispo-cust-salesperson-unlock">
                                    ändern (Filialleitung)
                                </button>
                            )}
                        </div>
                    ) : (
                        <select
                            value={salespersonId}
                            onChange={(e) => { touched.current = true; setSalespersonId(e.target.value); }}
                            disabled={!canChangeSeller && !!existingSeller?.id}
                            className="mt-1 w-full border border-gray-300 rounded-sm px-2 py-2 text-sm bg-white disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                            data-testid="dispo-cust-salesperson"
                        >
                            {existingSeller?.id && (
                                <option value={existingSeller.id}>{`${existingSeller.name || "Erfassender Verkäufer"} (hat den Verkauf erfasst)`}</option>
                            )}
                            {isCompanyLogin
                                ? <option value="">— Verkäufer wählen (Pflicht) —</option>
                                : <option value="self">{selfLabel}</option>}
                            {sellerEmps.filter((e) => e.id !== existingSeller?.id).map((e) => (
                                <option key={e.id} value={e.id}>{e.name}{e.employee_function === "verkaeufer" ? " · Verkäufer" : ""}</option>
                            ))}
                        </select>
                    )}
                    <div className="text-[10px] text-orange-700 mt-1" data-testid="dispo-cust-salesperson-count">
                        {existingSeller?.id && !sellerUnlocked
                            ? "Verkäufer ist nach der Erstauswahl festgeschrieben — Ansehen/Nachbearbeiten ändert ihn nicht. Änderung nur bewusst durch Filialleiter oder Inhaber/Geschäftsführer."
                            : existingSeller?.id
                                ? "ACHTUNG: Sie ändern den erfassten Verkäufer — die Änderung wird protokolliert."
                                : isCompanyLogin
                                    ? "Firmen-Login: Bitte einen Mitarbeiter als Verkäufer auswählen (Pflichtfeld) — der Firmenname kann nicht als Verkäufer erfasst werden."
                                    : (!empReady ? "Sie sind als Verkäufer vorausgewählt." : `Sie sind als Verkäufer vorausgewählt${canChangeSeller && sellerEmps.length > 0 ? ` (${sellerEmps.length + 1} wählbar)` : ""}.`)}
                    </div>
                </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    <div><Label className="text-xs">Anrede *</Label>
                        <select value={form.salutation} onChange={(e) => update("salutation", e.target.value)} className="mt-1 w-full border border-gray-300 rounded-sm px-2 py-2 text-sm bg-white" data-testid="dispo-cust-salutation">
                            {SALUTATIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </div>
                    <div className="flex flex-col justify-end">
                        <Label className="text-xs">Kundennummer</Label>
                        <div className="mt-1 h-[42px] flex items-center px-2 rounded-sm border border-dashed border-sky-300 bg-sky-50">
                            {custNumber
                                ? <span className="text-sm font-bold text-sky-700 flex items-center gap-1.5" data-testid="dispo-cust-number-inline"><IdCard className="w-4 h-4" /> {custNumber}</span>
                                : <span className="text-xs text-gray-400" data-testid="dispo-cust-number-inline">wird beim Speichern vergeben</span>}
                        </div>
                    </div>
                    <div><Label className="text-xs">Vorname *</Label><Input value={form.first_name} onChange={(e) => update("first_name", e.target.value)} data-testid="dispo-cust-first" /></div>
                    <div><Label className="text-xs">Nachname *</Label><Input value={form.last_name} onChange={(e) => update("last_name", e.target.value)} data-testid="dispo-cust-last" /></div>
                    <div><Label className="text-xs">Geburtsdatum * <span className="text-gray-400 font-normal">· Sicherheitsabfrage Upload-Portal</span></Label><Input type="date" value={form.birth_date || ""} onChange={(e) => update("birth_date", e.target.value)} data-testid="dispo-cust-birthdate" /></div>
                    {form.salutation === "Firma" && (
                        <div><Label className="text-xs">USt-IdNr. / Steuernummer *</Label>
                            <div className="flex gap-1.5 items-start">
                                <Input value={form.tax_id}
                                    onChange={(e) => { update("tax_id", e.target.value); setVatCheck(null); setForm((p) => ({ ...p, vat_check: null })); }}
                                    placeholder="z. B. ATU12345678" data-testid="dispo-cust-tax" />
                                {String(form.tax_id || "").trim().length >= 4 && form.country && form.country !== "Deutschland" && (
                                    <Button type="button" onClick={runVatCheck} disabled={vatChecking}
                                        className="shrink-0 h-10 bg-[#111827] hover:bg-black text-[#FFCC00] font-bold px-3"
                                        title="USt-IdNr. jetzt beim BZSt (eVATR) prüfen" data-testid="dispo-cust-vat-check-btn">
                                        {vatChecking ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ShieldCheck className="w-4 h-4 mr-1" /> Prüfen</>}
                                    </Button>
                                )}
                            </div>
                            {(!form.country || form.country === "Deutschland") && (
                                <div className="text-[10px] text-gray-400 mt-0.5" data-testid="dispo-cust-vat-check-domestic-hint">
                                    Prüfung nur nötig, wenn das Fahrzeug ins Ausland verkauft wird (Land unten wählen).
                                </div>
                            )}
                            {vatCheck && (
                                <div className={`mt-1 text-[11px] rounded-sm border px-2 py-1.5 flex items-start gap-1.5 ${vatCheck.valid ? "bg-emerald-50 border-emerald-300 text-emerald-800" : vatCheck.valid === false ? "bg-red-50 border-red-300 text-red-800" : "bg-amber-50 border-amber-300 text-amber-800"}`} data-testid="dispo-cust-vat-check-result">
                                    {vatCheck.valid ? <ShieldCheck className="w-3.5 h-3.5 shrink-0 mt-0.5" /> : <ShieldAlert className="w-3.5 h-3.5 shrink-0 mt-0.5" />}
                                    <span>
                                        <b>{vatCheck.valid ? "Gültig ✓" : vatCheck.valid === false ? "Ungültig" : "Nicht prüfbar"}</b> — {vatCheck.meldung}
                                        {vatCheck.name ? <> · {vatCheck.name}</> : null}
                                        <span className="text-[10px] opacity-70"> ({vatCheck.source === "evatr" ? "BZSt/eVATR" : "VIES"} · {vatCheck.checked_at ? new Date(vatCheck.checked_at).toLocaleString("de-DE") : ""})</span>
                                    </span>
                                </div>
                            )}
                        </div>
                    )}
                    {form.salutation === "Firma" && (
                    <div className="sm:col-span-2">
                        <Label className="text-xs">Firma * <span className="text-gray-400 font-normal">· Suche: registrierte User &amp; Gewerbekunden</span></Label>
                        <div className="relative">
                            <Input value={form.company_name} onChange={(e) => updateCompany(e.target.value)} placeholder="Firma suchen oder eingeben…" data-testid="dispo-cust-company" />
                            {firmaSearching && <Loader2 className="w-4 h-4 animate-spin absolute right-2 top-2.5 text-gray-400" />}
                            {firmaResults.length > 0 && (
                                <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-sm shadow-lg max-h-56 overflow-y-auto" data-testid="dispo-cust-firma-results">
                                    {firmaResults.map((r) => (
                                        <button key={`${r.kind}-${r.id}`} type="button" onClick={() => selectFirma(r)} className="w-full text-left px-3 py-2 hover:bg-indigo-50 border-b border-gray-100 last:border-0" data-testid={`dispo-cust-firma-result-${r.id}`}>
                                            <div className="text-sm font-bold text-gray-800 flex items-center gap-1.5">
                                                <span className="truncate">{r.company_name || `${r.first_name} ${r.last_name}`.trim() || r.email}</span>
                                                {r.kind === "registered_user"
                                                    ? <span className="text-[9px] uppercase font-black px-1 py-0.5 rounded-sm bg-emerald-100 text-emerald-700 shrink-0">Registrierter User</span>
                                                    : <span className="text-[9px] uppercase font-black px-1 py-0.5 rounded-sm bg-gray-100 text-gray-600 shrink-0">Gewerbekunde</span>}
                                            </div>
                                            <div className="text-[11px] text-gray-500">{[r.email, r.city, r.transaction_code].filter(Boolean).join(" · ")}</div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                    )}
                    {form.salutation === "Firma" && (
                        <div><Label className="text-xs">E-Mail *</Label><Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="dispo-cust-email" /></div>
                    )}
                    {form.salutation === "Firma" && form.customer_kind === "registered_user" ? (
                        <div className="sm:col-span-2 lg:col-span-3 bg-emerald-50 border border-emerald-200 rounded-sm p-3" data-testid="dispo-cust-registered-block">
                            <div className="text-sm font-bold text-emerald-900 flex items-center gap-1.5"><IdCard className="w-4 h-4" /> Registrierter User übernommen</div>
                            <p className="text-[11px] text-emerald-700 mt-1">
                                {form.transaction_code ? <>Transaktionscode <b>{form.transaction_code}</b> übernommen. </> : null}
                                Das B2B-Händlergeschäft-Feld entfällt; ein Gewerbenachweis ist nicht nötig. Die <b>Ankaufrechnung</b> wird nur bei Ankauf / Inzahlungnahme angefordert.
                            </p>
                        </div>
                    ) : form.salutation === "Firma" ? (
                        <div className="sm:col-span-2 lg:col-span-3 bg-indigo-50 border border-indigo-200 rounded-sm p-3" data-testid="dispo-cust-b2b-block">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input type="checkbox" checked={!!form.is_b2b_dealer} onChange={(e) => update("is_b2b_dealer", e.target.checked)} className="w-4 h-4 accent-indigo-600" data-testid="dispo-cust-b2b-checkbox" />
                                <span className="text-sm font-bold text-indigo-900 flex items-center gap-1.5"><Building2 className="w-4 h-4" /> B2B Händlergeschäft</span>
                            </label>
                            <p className="text-[11px] text-indigo-700 mt-1">Verkauf an einen anderen Händler (B2B). Relevant für Differenzbesteuerung/Reverse-Charge, Bonität und Auswertungen. Im Upload-Portal erscheint <b>Handelsregister/Gewerbeanmeldung</b>.</p>
                        </div>
                    ) : null}
                    {form.salutation !== "Firma" && (
                        <div><Label className="text-xs">E-Mail *</Label><Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="dispo-cust-email" /></div>
                    )}
                    <div><Label className="text-xs">Telefon *</Label><Input value={form.phone} onChange={(e) => update("phone", e.target.value)} data-testid="dispo-cust-phone" /></div>
                    <div className="sm:col-span-2 grid grid-cols-3 gap-2">
                        <div className="col-span-2"><Label className="text-xs">Straße *</Label><Input value={form.street} onChange={(e) => update("street", e.target.value)} data-testid="dispo-cust-street" /></div>
                        <div><Label className="text-xs">Nr. *</Label><Input value={form.house_number} onChange={(e) => update("house_number", e.target.value)} data-testid="dispo-cust-house" /></div>
                    </div>
                    <div><Label className="text-xs">PLZ *</Label><Input value={form.postal_code} onChange={(e) => update("postal_code", e.target.value)} data-testid="dispo-cust-plz" /></div>
                    <div><Label className="text-xs">Ort *</Label><Input value={form.city} onChange={(e) => update("city", e.target.value)} data-testid="dispo-cust-city" /></div>
                    <div><Label className="text-xs">Land *</Label>
                        <select value={form.country || "Deutschland"} onChange={(e) => update("country", e.target.value)}
                            className="mt-1 w-full border border-gray-300 rounded-sm px-2 py-2 text-sm bg-white" data-testid="dispo-cust-country">
                            {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                        {form.country && form.country !== "Deutschland" && form.salutation === "Firma" && (
                            <div className="text-[10px] text-sky-700 mt-0.5" data-testid="dispo-cust-eu-hint">
                                EU-Ausland: mit geprüfter USt-IdNr. ist der Verkauf steuerfrei (netto, §6a UStG).
                            </div>
                        )}
                    </div>
                </div>
                <DialogFooter>
                    <DispoBackButton onBack={onBack ? () => { persist({ silent: true }); onBack(); } : undefined} />
                    <Button variant="outline" onClick={onClose}>Abbrechen</Button>
                    <Button onClick={() => persist({ silent: false, advance: true })} disabled={saving} data-testid="dispo-cust-submit">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Weiter"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
